import { route } from 'preact-router';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { Button } from '../../components/ui/Button.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { api } from '../../services/api.js';
import { fileFrom, odometerValue, refusalWording, uploadPhoto } from './InspectionCapturePage.js';

/**
 * 415 (#1520): the fuel photo. Before the key is out of the lockbox the car cannot be started, so the dashboard is
 * asked here, after the code: one photo with the engine on, sent once, inside the pickup window. The platform decides
 * whether it may be sent; this screen says what it was told. Nothing waits on it.
 *
 * 423 (#1546, mock-up frame 3): the odometer reading the customer reads in the car comes with it, required; both are
 * sent together, once.
 */
export function FuelPhotoPage({ id }: { id?: string; path?: string }) {
  const [file, setFile] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [odometer, setOdometer] = useState('');
  const reading = odometerValue(odometer);
  const readingError =
    odometer.trim() !== '' && reading === null ? 'Whole kilometres only, numbers only.' : null;
  const ready = file !== null && reading !== null;
  const preview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(
    () => () => {
      if (preview) URL.revokeObjectURL(preview);
    },
    [preview],
  );

  async function submit() {
    if (!file || reading === null || sending) return;
    setError(null);
    setSending(true);
    try {
      const photoId = await uploadPhoto(file);
      await api.post(`/bookings/${id}/inspections/fuel`, {
        photo_id: photoId,
        odometer_km: reading,
      });
      route(`/booking/${id}/confirm`);
    } catch (err) {
      setError(refusalWording(err));
      setSending(false);
    }
  }

  return (
    <div className="bg-bg px-4 py-6">
      <PageHeader title="Fuel and odometer" fallback={`/booking/${id}/confirm`} />
      <p className="mb-3 text-sm text-text-muted">
        One photo of the dashboard with the engine on, and the odometer reading as shown in the car.
      </p>
      <label
        data-testid="fuel-shot"
        className={`relative flex aspect-[4/3] cursor-pointer flex-col items-center justify-center gap-1 overflow-hidden rounded-xl text-xs text-text-muted ${
          preview ? 'bg-surface-mid' : 'border-[1.5px] border-dashed border-border bg-surface-mid'
        }`}
      >
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          capture="environment"
          className="sr-only"
          aria-label="Dashboard photo"
          onChange={(e) => {
            const f = fileFrom(e);
            if (f) setFile(f);
          }}
        />
        {preview ? (
          <img
            src={preview}
            alt="Dashboard"
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <>
            <span className="text-2xl" aria-hidden="true">
              📷
            </span>
            <span className="font-bold text-text">Dashboard</span>
            <span>fuel gauge visible, engine on</span>
          </>
        )}
      </label>
      <div className="mt-4">
        <label htmlFor="odometer-reading" className="mb-1.5 block text-sm font-bold text-text">
          Odometer reading
        </label>
        <div
          className={`flex items-center rounded-lg border-[1.5px] bg-surface-mid px-3 ${
            readingError ? 'border-error' : 'border-border'
          }`}
        >
          <input
            id="odometer-reading"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            placeholder="e.g. 48213"
            value={odometer}
            onInput={(e) => setOdometer((e.target as HTMLInputElement).value)}
            aria-describedby="odometer-hint"
            aria-invalid={readingError ? 'true' : undefined}
            className="min-w-0 flex-1 bg-transparent py-3 text-base text-text outline-none"
          />
          <span className="text-sm text-text-muted">km</span>
        </div>
        {readingError ? (
          <p id="odometer-hint" data-testid="odometer-error" className="mt-1.5 text-xs text-error">
            {readingError}
          </p>
        ) : (
          <p
            id="odometer-hint"
            data-testid="odometer-hint"
            className="mt-1.5 text-xs text-text-muted"
          >
            Type the number on the dashboard, not the trip meter.
          </p>
        )}
      </div>
      {error && (
        <p role="alert" data-testid="fuel-error" className="mt-3 text-sm text-error">
          {error}
        </p>
      )}
      <Button
        data-testid="fuel-submit"
        disabled={!ready}
        loading={sending}
        onClick={() => void submit()}
        size="lg"
        className="mt-3 w-full"
      >
        Send fuel photo and reading
      </Button>
      <p className="mt-2 text-center text-xs text-text-muted">You can send this once.</p>
    </div>
  );
}
